import type {
  AdminRunMarkerKey,
  AdminRunSegmentKey,
  AdminRunTelemetry,
} from '@/api/adminClient';

export const MARKER_LABELS: Record<AdminRunMarkerKey, string> = {
  'prompt.submitted': 'Prompt submitted',
  'plan.approved': 'Build clicked',
  'run.started': 'Run workflow started',
  'seat.queued': 'Queued for a seat',
  'seat.claim_started': 'Seat requested',
  'seat.ready': 'Seat ready',
  'sandbox.ready': 'Sandbox ready',
  'agent.editing.start': 'Agent started',
  'agent.start_context': 'Start context assembled',
  'agent.resume_context': 'Prompt handed to agent',
  'llm.request_sent': 'LLM request sent',
  'llm.first_token': 'First model output',
  'agent.first_write_started': 'Started writing a file',
  'agent.first_tool': 'First tool finished',
  'agent.first_edit': 'First file saved',
  'agent.editing.complete': 'Agent finished',
  'preview.ready': 'Preview ready',
  'run.failed': 'Run failed',
};

type SegmentMeta = { label: string; hint: string; swatch: string };

export const SEGMENT_META: Record<AdminRunSegmentKey, SegmentMeta> = {
  handoff: {
    label: 'Handoff',
    hint: 'API → Temporal → seat request. Queueing and workflow start before a seat was asked for.',
    swatch: 'bg-zinc-600',
  },
  handoff_and_seat: {
    label: 'Handoff + seat',
    hint: 'Workflow start and seat claim together — this run did not report a separate claim time.',
    swatch: 'bg-zinc-600',
  },
  seat_queue: {
    label: 'Seat queue',
    hint: 'No preview seat was free; the run waited in the seat queue.',
    swatch: 'bg-chart-5',
  },
  seat_claim: {
    label: 'Seat claim',
    hint: 'Waiting for a preview seat to bind the workspace and pass Vite / App Service readiness.',
    swatch: 'bg-chart-4',
  },
  agent_boot: {
    label: 'Seat → agent',
    hint: 'Between the seat reporting ready and the executor starting the agent turn.',
    swatch: 'bg-zinc-400',
  },
  context_prep: {
    label: 'Context prep',
    hint: 'Recipe pre-scaffold, start-context packet and write-first gate before the prompt is handed over.',
    swatch: 'bg-chart-2',
  },
  model_ttft: {
    label: 'Model time-to-first-token',
    hint: 'OpenCode start plus the model working on the first request before streaming anything.',
    swatch: 'bg-warning',
  },
  first_token_to_tool: {
    label: 'First output → first tool',
    hint: 'The model was streaming (text or reasoning) before it called its first tool.',
    swatch: 'bg-dispatch',
  },
  model_and_agent: {
    label: 'Agent + model (no token telemetry)',
    hint: 'No first-token signal for this run, so OpenCode start, time-to-first-token and any thinking are one span.',
    swatch: 'bg-warning',
  },
  explore_before_edit: {
    label: 'First tool → first edit',
    hint: 'Reads and exploration between the first tool call and the first file write landing.',
    swatch: 'bg-brand',
  },
  until_failure: {
    label: 'Until failure',
    hint: 'The run failed before the agent called a tool.',
    swatch: 'bg-destructive',
  },
};

export function formatDurationMs(ms: number | undefined | null): string {
  if (ms === undefined || ms === null || !Number.isFinite(ms)) return '—';
  const abs = Math.abs(ms);
  const sign = ms < 0 ? '−' : '';
  if (abs < 1000) return `${sign}${Math.round(abs)} ms`;
  if (abs < 60_000) return `${sign}${(abs / 1000).toFixed(abs < 10_000 ? 2 : 1)} s`;
  const minutes = Math.floor(abs / 60_000);
  const seconds = Math.round((abs % 60_000) / 1000);
  if (seconds === 60) return `${sign}${minutes + 1}m 0s`;
  return `${sign}${minutes}m ${seconds}s`;
}

export function formatOffsetMs(ms: number | null | undefined): string {
  if (ms === undefined || ms === null || !Number.isFinite(ms)) return '';
  if (ms < 0) return `−${(Math.abs(ms) / 1000).toFixed(1)}s`;
  return `+${(ms / 1000).toFixed(1)}s`;
}

export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function formatTokens(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
}

export function formatClockTime(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 1,
  }).format(date);
}

export type StatusTone = 'success' | 'warning' | 'danger' | 'active' | 'neutral';

export function runStatusTone(status: string | undefined): StatusTone {
  switch (status) {
    case 'preview_ready':
      return 'success';
    case 'failed':
    case 'blocked':
      return 'danger';
    case 'running':
    case 'validating':
    case 'snapshotting':
      return 'active';
    case 'queued':
      return 'warning';
    default:
      return 'neutral';
  }
}

const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  validating: 'Validating',
  snapshotting: 'Saving',
  preview_ready: 'Preview ready',
  failed: 'Failed',
  blocked: 'Blocked',
};

export function runStatusLabel(status: string | undefined): string {
  if (!status) return 'Unknown';
  return STATUS_LABELS[status] ?? humanizeKey(status);
}

const ENGINE_LABELS: Record<string, string> = { opencode: 'OpenCode', omp: 'OMP' };

/** The coding agent's product name; runs from before the choice existed ran on OpenCode. */
export function engineLabel(engine: string | undefined): string {
  const key = (engine ?? '').trim().toLowerCase() || 'opencode';
  return ENGINE_LABELS[key] ?? engine ?? 'OpenCode';
}

const ENGINE_FALLBACK_LABELS: Record<string, string> = {
  guest_without_omp: 'OMP requested, ran on OpenCode — workspace too old',
};

/** The coding agent a run ran on, and why it was not OMP when it asked for OMP. */
export function runEngineLabel(engine: string | undefined, fallback: string | undefined): string {
  return (fallback && ENGINE_FALLBACK_LABELS[fallback]) || engineLabel(engine);
}

/**
 * The coding agent a run asked for and, when the guest reported a different one, what it
 * actually ran on, so a comparison never counts a run under the wrong agent.
 */
export function engineSummary(model: AdminRunTelemetry['model']): string {
  if (model.engineFallback && ENGINE_FALLBACK_LABELS[model.engineFallback]) return ENGINE_FALLBACK_LABELS[model.engineFallback];
  const requested = engineLabel(model.engine);
  const ran = model.backend ? engineLabel(model.backend) : undefined;
  return ran && ran !== requested ? `${requested} requested, ran on ${ran}` : `on ${ran ?? requested}`;
}

/** "OMP retried 2× · compacted 1×": how often OMP recovered on its own during the run. */
export function ompRecoverySummary(omp: AdminRunTelemetry['omp']): string | undefined {
  if (!omp) return undefined;
  const parts = [
    omp.retries > 0 ? `retried ${omp.retries}×` : undefined,
    omp.compactions > 0 ? `compacted ${omp.compactions}×` : undefined,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? `OMP ${parts.join(' · ')}` : undefined;
}

export function humanizeKey(key: string): string {
  const words = key.replace(/[._]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

const EVENT_LABELS: Record<string, string> = {
  'prompt.submitted': 'Prompt submitted',
  'plan.approved': 'Build clicked (plan approved)',
  'run.started': 'Run started',
  'run.plane.selected': 'Execution plane selected',
  'run.plane.fallback': 'Fell back to job plane',
  'run.plane.retry': 'Workspace claimed again',
  'run.seat.pending': 'Waiting for a seat',
  'run.seat.unavailable': 'No seat available',
  'run.sandbox.unavailable': 'Sandbox unavailable',
  'sandbox.seat_pending': 'Waiting for a seat',
  'sandbox.rebound': 'Seat claimed',
  'sandbox.claim_failed': 'Seat claim failed',
  'sandbox.ready': 'Sandbox ready',
  'preview.hot.ready': 'Live preview ready',
  'preview.app_service.ready': 'App Service ready',
  'preview.app_service.failed': 'App Service failed',
  'recipe.prescaffold': 'First page pre-scaffolded',
  'agent.editing.start': 'Agent started',
  'agent.start_context': 'Start context assembled',
  'agent.resume_context': 'Prompt handed to agent',
  'agent.write_gate.start': 'Write-first gate armed',
  'agent.write_gate.violation': 'Write-first gate violation',
  'agent.write_gate.correction': 'Write-first gate correction',
  'agent.write_gate.result': 'Write-first gate result',
  'agent.llm.request_sent': 'LLM request sent',
  'agent.llm.first_token': 'First model token',
  'agent.first_write_started': 'Started writing a file',
  'agent.first_tool': 'First tool finished',
  'agent.tool': 'Tool call',
  'agent.first_edit_completed': 'First file saved',
  'agent.explore_loop': 'Explore loop detected',
  'agent.edit_metrics': 'Edit metrics',
  'agent.turn.stalled': 'Agent stalled',
  'agent.engine.fallback': 'OMP unavailable on this workspace, ran on OpenCode',
  'agent.omp.retry': 'OMP retried a model call',
  'agent.omp.fallback': 'OMP switched to a fallback model',
  'agent.omp.compaction': 'OMP compacted its context',
  'agent.omp.persistence': 'OMP session file trouble',
  'agent.turn.budget_exceeded': 'Turn budget exceeded',
  'agent.turn.abort_timeout': 'Turn aborted (timeout)',
  'agent.editing.complete': 'Agent finished',
  'agent.todos': 'Agent to-dos updated',
  'dependencies.install.start': 'Installing dependencies',
  'dependencies.install.complete': 'Dependencies installed',
  'dependencies.install.failed': 'Dependency install failed',
  'quality.checking': 'Quality checks running',
  'quality.repairing': 'Quality repair',
  'quality.completed': 'Quality checks finished',
  'quality.findings': 'Saved-version check result',
  'quality.repair_started': 'Background structural repair started',
  'workspace.synced': 'Workspace synced',
  'workspace.sync.failed': 'Workspace sync failed',
  'preview.snapshot.building': 'Building preview snapshot',
  'preview.snapshot.ready': 'Preview snapshot ready',
  'preview.snapshot.failed': 'Preview snapshot failed',
  'run.preview_ready': 'Preview ready',
  'preview.ready': 'Preview ready',
  'run.failed': 'Run failed',
  'run.cancelled': 'Run cancelled',
  'run.explanation': 'Run explanation',
};

export function eventLabel(key: string): string {
  return EVENT_LABELS[key] ?? humanizeKey(key);
}

export function eventTone(key: string): StatusTone {
  if (/(failed|violation|unavailable|stalled|exceeded|abort|cancelled)/.test(key)) return 'danger';
  if (/(explore_loop|pending|fallback|correction)/.test(key)) return 'warning';
  if (/(ready|complete|landed|first_tool|first_edit|first_token|first_write)/.test(key)) return 'success';
  return 'neutral';
}

const SUMMARY_FIELDS = [
  'tool',
  'title',
  'status',
  'model',
  'backend',
  'outcome',
  'mode',
  'reason',
  'seat',
  'claimWaitMs',
  'elapsedMs',
  'durationMs',
  'bytes',
  'files',
  'cacheHits',
  'cacheMisses',
  'ttftMs',
  'firstToolMs',
  'readsBeforeEdit',
  'recipe',
  'plane',
  'waitedMs',
  'error',
] as const;

/** The few payload fields worth reading at a glance; the full payload stays one click away. */
export function eventSummaryChips(payload: Record<string, unknown>): Array<{ key: string; value: string }> {
  const chips: Array<{ key: string; value: string }> = [];
  for (const key of SUMMARY_FIELDS) {
    const value = payload[key];
    if (value === undefined || value === null || value === '') continue;
    if (typeof value === 'object') continue;
    let text = String(value);
    if (typeof value === 'number' && /Ms$/.test(key)) text = formatDurationMs(value);
    else if (typeof value === 'number' && key === 'bytes') text = formatBytes(value);
    chips.push({ key, value: text.length > 80 ? `${text.slice(0, 80)}…` : text });
    if (chips.length >= 5) break;
  }
  return chips;
}

export type WaitVerdict = {
  segment: AdminRunSegmentKey;
  durationMs: number;
  share: number;
  sentence: string;
};

/** Names the phase that dominated the wait, in words, from the reported breakdown only. */
export function waitVerdict(telemetry: AdminRunTelemetry): WaitVerdict | undefined {
  const breakdown = telemetry.waitBreakdown;
  if (!breakdown || breakdown.totalMs <= 0 || breakdown.segments.length === 0) return undefined;
  const largest = breakdown.segments.reduce((best, segment) =>
    segment.durationMs > best.durationMs ? segment : best,
  );
  const share = largest.durationMs / breakdown.totalMs;
  const label = SEGMENT_META[largest.key].label.toLowerCase();
  const amount = `${formatDurationMs(largest.durationMs)} of ${formatDurationMs(breakdown.totalMs)}`;
  const lead =
    share >= 0.5
      ? `Most of the wait was ${label}: ${amount} (${Math.round(share * 100)}%).`
      : `No single phase dominated; the largest was ${label} at ${amount} (${Math.round(share * 100)}%).`;
  const notes: string[] = [];
  if (
    largest.key === 'seat_queue' &&
    telemetry.seat.queuedMs !== undefined &&
    Math.abs(telemetry.seat.queuedMs - largest.durationMs) >= 1000
  ) {
    notes.push(`The seat queue reported ${formatDurationMs(telemetry.seat.queuedMs)} of waiting.`);
  }
  if (largest.key === 'model_ttft' || largest.key === 'model_and_agent') {
    const seat = telemetry.seat.claimMs;
    if (seat !== undefined) notes.push(`The seat was ready after ${formatDurationMs(seat)}, so this was not a seat wait.`);
    if (telemetry.startContext?.bytes !== undefined) {
      notes.push(`The first request carried a ${formatBytes(telemetry.startContext.bytes)} start context.`);
    }
  }
  const exploration = telemetry.exploration;
  if (exploration?.exploreLoopDetected) {
    notes.push(`An explore loop was detected (${exploration.readsBeforeEdit ?? 'several'} reads before the first edit).`);
  } else if (exploration?.readsBeforeEdit === 0) {
    notes.push('No reads before the first edit, so exploration was not a factor.');
  }
  return {
    segment: largest.key,
    durationMs: largest.durationMs,
    share,
    sentence: [lead, ...notes].join(' '),
  };
}

export function markerOffset(telemetry: AdminRunTelemetry, key: AdminRunMarkerKey): number | undefined {
  const marker = telemetry.markers.find((row) => row.key === key);
  return marker?.offsetMs ?? undefined;
}
